import { useState } from 'react';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { fmt } from '../lib/format';
import { generateReport } from '../lib/api';

interface Props {
  onClose(): void;
  onGenerated(reportId: string): void;
}

const PRESETS = [1, 2, 3] as const;
type Preset = (typeof PRESETS)[number];

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

// Mirror the backend window: rolling `today − months×30` days, inclusive of today.
function presetStartIso(months: number): string {
  const d = new Date();
  d.setDate(d.getDate() - months * 30);
  return d.toISOString();
}

export function GenerateModal({ onClose, onGenerated }: Props): JSX.Element {
  const today = isoToday();
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [months, setMonths] = useState<Preset>(3);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customValid = Boolean(from && to && from <= to && to <= today);
  const canSubmit = !running && (mode === 'preset' || customValid);

  async function submit() {
    setRunning(true);
    setError(null);
    try {
      const req = mode === 'custom' ? { dateFrom: from, dateTo: to } : { rangeMonths: months };
      const res = await generateReport(req);
      onGenerated(res.reportId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setRunning(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="modal-hd">
        <div className="eb">New report</div>
        <div className="ttl">Generate new report</div>
      </div>
      <div className="modal-bd">
        {error && (
          <div className="banner err">
            <span className="dot" />
            <span>{error}</span>
          </div>
        )}
        <div>
          <label className="input-label">Date range</label>
          <div className="segmented">
            {PRESETS.map((v) => (
              <button
                key={v}
                className={mode === 'preset' && months === v ? 'on' : ''}
                onClick={() => {
                  setMode('preset');
                  setMonths(v);
                }}
              >
                {v === 1 ? '1 month' : `${v} months`}
              </button>
            ))}
            <button className={mode === 'custom' ? 'on' : ''} onClick={() => setMode('custom')}>
              Custom
            </button>
          </div>

          {mode === 'custom' && (
            <div className="fs-row" style={{ marginTop: 8 }}>
              <input
                type="date"
                className="input"
                aria-label="From date"
                value={from}
                max={to || today}
                onChange={(e) => setFrom(e.target.value)}
              />
              <input
                type="date"
                className="input"
                aria-label="To date"
                value={to}
                max={today}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          )}

          <div className="d-meta" style={{ marginTop: 8, fontSize: 12 }}>
            {mode === 'preset' ? (
              <>
                Pulling sessions from{' '}
                <span className="d-mono" style={{ color: 'var(--ink)' }}>{fmt.date(presetStartIso(months))}</span> to{' '}
                <span className="d-mono" style={{ color: 'var(--ink)' }}>{fmt.date(new Date().toISOString())}</span>.
              </>
            ) : customValid ? (
              <>
                Pulling sessions from{' '}
                <span className="d-mono" style={{ color: 'var(--ink)' }}>{fmt.date(`${from}T00:00:00`)}</span> to{' '}
                <span className="d-mono" style={{ color: 'var(--ink)' }}>{fmt.date(`${to}T00:00:00`)}</span>.
              </>
            ) : (
              <span style={{ color: 'var(--accent)' }}>
                Pick a start and end date (start on or before end; end no later than today).
              </span>
            )}
          </div>
        </div>
        <div>
          <label className="input-label">Outputs</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
            <input type="checkbox" defaultChecked disabled style={{ accentColor: 'var(--mute)' }} />
            <span>Save to history <span style={{ color: 'var(--mute)' }}>· always on</span></span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" defaultChecked style={{ accentColor: 'var(--accent)' }} />
            <span>Upload .xlsx to Storage</span>
          </label>
        </div>
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!canSubmit} onClick={submit}>
          {running ? <><span className="spinner" />Generating…</> : 'Generate'}
        </Button>
      </div>
    </Modal>
  );
}
